import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FAILED, IDLE, LOADING, NEEDS_REFRESH, SUCCEEDED } from "./constants";

import {
  getAllAnomalyScores,
  saveAllScoresInformation,
} from "../../utils/api/RefundAnalysisApi";
import { RootState } from "../store/store";
import { getOktaUser } from "../../utils/LocalStorage";
import RefundAnomalyScores from "../../interfaces/RefundAnomalyScores";
import RefundAnomaly from "../../interfaces/RefundAnomaly";

export interface RefundAnalysisState {
  refundAnomalyFeedback: RefundAnomalyScores;
  duplicateRequestedRefundAnomalyFeedbackList: Array<RefundAnomaly>;
  frcList: string[];
  userEmailValidated: boolean;
  locationTypes: string[];
  countryCodes: string[];
  selectedUnit: any;
  selectedUnitStatus: string;
  bulkUploadStatus: string;
  saveAllStatus: string;
  fetchAllRefundAnalysisStatus: string;
  status: string;
  error: any;
  lastUpdated: string;
}

const initialState: RefundAnalysisState = {
  // @ts-ignore
  refundAnomalyFeedback: [],
  duplicateRequestedRefundAnomalyFeedbackList: [],
  frcList: [],
  userEmailValidated: true,
  locationTypes: [],
  countryCodes: [],
  selectedUnit: null,
  selectedUnitStatus: IDLE,
  bulkUploadStatus: IDLE,
  saveAllStatus: IDLE,
  fetchAllRefundAnalysisStatus: IDLE,
  status: IDLE,
  error: null,
  lastUpdated: "",
};

export const saveAllRefundAnalysisInformation = createAsyncThunk(
  "/save-all",
  async (sherlockFeedback: any, thunkAPI) => {
    const userName = getOktaUser()[2];
    const userEmail = getOktaUser()[1];
    const userEmployeeId = getOktaUser()[3];
    const request: any = {
      userName,
      userEmail,
      userEmployeeId,
      sherlockFeedback,
    };

    let response;

    const importArr: any = [];
    importArr.push(sherlockFeedback);

    response = await saveAllScoresInformation(request);
    return response;
  }
);

export const fetchAllRefundAnalysisInformation = createAsyncThunk(
  "/refund-anomaly-scores",
  async (
    params: { userEmail: string; startDate: string; endDate: string },
    thunkAPI
  ) => {
    const result: any = await getAllAnomalyScores(
      params.userEmail,
      params.startDate,
      params.endDate
    );
    const scoreListByLocation = result.refundAnomalies;
    let frcList: string[] = ["All"];
    let userEmailValidated = result.isVerified;
    for (let location of scoreListByLocation) {
      if (location.fsdmAnomalyScores) {
        location.runningRiskRankSum = 0;
        location.runningRiskProbSum = 0;
        location.countHighRiskScores = location.fsdmAnomalyScores.length;
        location.dateLastEmailOperator = "";
        location.operatorName = location.fsdmAnomalyScores[0].operatorName;
        location.emailOperator = false;
        location.modelProb = 0;
        location.avgRiskRank = 0;
        location.avgRiskProb = 0;
        location.emailFrc = false;
        location.ranked = "";
        location.refundType = "";
        location.minTransDate = "";
        location.maxTransDate = "";
        location.hasUnEmailedTransactions = false;
        location.hasEmailedTransactions = false;
        location.outputRefundAmount = 0;
        if (location.fsdmAnomalyScores)
          var dates = location.fsdmAnomalyScores.map(function (x: {
            businessDate: string | number | Date;
          }) {
            return new Date(x.businessDate);
          });
        location.maxTransDate = new Date(Math.max.apply(null, dates))
          .toISOString()
          .split("T")[0];
        location.minTransDate = new Date(Math.min.apply(null, dates))
          .toISOString()
          .split("T")[0];

        for (let anomalyScore of location.fsdmAnomalyScores) {
          anomalyScore.outputRefundAmount = +anomalyScore.outputRefundAmount;
          anomalyScore.modelDailyRank = +anomalyScore.modelDailyRank;

          if (!location.refundType.includes(anomalyScore.refundType)) {
            location.refundType += anomalyScore.refundType;
          }

          let n = Number(anomalyScore.modelProb);
          anomalyScore.modelProb = +n.toFixed(
            1 - Math.floor(Math.log(n) / Math.log(10))
          );

          if (anomalyScore.modelProb > location.modelProb) {
            location.modelProb = anomalyScore.modelProb;
          }

          location.runningRiskRankSum += +anomalyScore.modelDailyRank;
          location.runningRiskProbSum += +anomalyScore.modelProb;

          if (
            parseInt(anomalyScore.outputRefundAmount) <
            +location.outputRefundAmount
          ) {
            location.outputRefundAmount = anomalyScore.outputRefundAmount;
          }

          anomalyScore.emailOperator = false;
          anomalyScore.emailFrc = false;

          if (!anomalyScore.refundAnomalyScoreRead) {
            location.hasUnEmailedTransactions = true;
            anomalyScore.hasEmailedOperator = false;
            if (!location.ranked.includes("Unranked")) {
              location.ranked += " Unranked ";
            }
            anomalyScore.refundAnomalyScoreRead = {
              riskLevel: 0,
            };
          } else {
            if (
              anomalyScore.refundAnomalyScoreRead.dateLastEmailOperator !== null
            ) {
              if (
                location.dateLastEmailOperator === "" ||
                anomalyScore.refundAnomalyScoreRead.dateLastEmailOperator >
                  location.dateLastEmailOperator
              ) {
                location.dateLastEmailOperator =
                  anomalyScore.refundAnomalyScoreRead.dateLastEmailOperator;
              }

              location.hasEmailedTransactions = true;
              anomalyScore.hasEmailedOperator = true;
            } else {
              location.hasUnEmailedTransactions = true;
              anomalyScore.hasEmailedOperator = false;
            }
            if (!location.ranked.includes("Ranked")) {
              location.ranked += " Ranked ";
            }
          }
          if (!frcList.includes(anomalyScore.financialConsultant)) {
            frcList.push(anomalyScore.financialConsultant);
          }
          location.financialConsultant = anomalyScore.financialConsultant;
          location.rcma = anomalyScore.rcma;
          if (location.dateLastEmailOperator !== "") {
            location.dateLastEmailOperator =
              location.dateLastEmailOperator.split("T")[0];
          }
        }
      }
      location.avgRiskRank = (
        location.runningRiskRankSum / location.fsdmAnomalyScores.length
      ).toFixed(2);

      location.avgRiskProb = Number(
        (
          location.runningRiskProbSum / location.fsdmAnomalyScores.length
        ).toPrecision(2)
      );
    }
    scoreListByLocation.detailFilterModels = [];
    return { scoreListByLocation, frcList, userEmailValidated };
  }
);

function compare(currentScore: number, newScore: number) {
  return currentScore > newScore ? currentScore : newScore;
}

function compareDate(date1: string, date2: any) {
  return date1 > date2 ? date1 : date2;
}

function gridDateFormatter(date: string) {
  if (date == null) {
    return "";
  }
  if (date.indexOf("T") == -1) {
    return date;
  }
  if (date.length != 0) {
    date = date.slice(0, date.indexOf("T"));
    return date;
  } else {
    return "";
  }
}
export const refundAnalysisSlice = createSlice({
  name: "RefundAnalysisInformation",
  initialState,
  reducers: {
    alertRefreshScoresInformation: (state) => {
      state.fetchAllRefundAnalysisStatus = NEEDS_REFRESH;
    },
    updateScoresInformation: (state, action) => {
      state.refundAnomalyFeedback = action.payload;
    },
    resetBulkUploadStatus: (state) => {
      state.bulkUploadStatus = IDLE;
    },
    resetSaveAllStatus: (state) => {
      state.saveAllStatus = IDLE;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAllRefundAnalysisInformation.pending, (state, action) => {
        state.fetchAllRefundAnalysisStatus = LOADING;
      })
      .addCase(fetchAllRefundAnalysisInformation.fulfilled, (state, action) => {
        state.fetchAllRefundAnalysisStatus = SUCCEEDED;
        state.refundAnomalyFeedback = action.payload.scoreListByLocation;
        state.frcList = action.payload.frcList;
        state.userEmailValidated = action.payload.userEmailValidated;
      })
      .addCase(fetchAllRefundAnalysisInformation.rejected, (state, action) => {
        state.fetchAllRefundAnalysisStatus = FAILED;
        state.error = action.error.message;
      })
      .addCase(saveAllRefundAnalysisInformation.pending, (state, action) => {
        state.saveAllStatus = LOADING;
      })
      .addCase(saveAllRefundAnalysisInformation.fulfilled, (state, action) => {
        state.saveAllStatus = SUCCEEDED;
        state.duplicateRequestedRefundAnomalyFeedbackList = action.payload;
      })
      .addCase(saveAllRefundAnalysisInformation.rejected, (state, action) => {
        state.saveAllStatus = FAILED;
        state.error = action.error.message;
      });
  },
});

export default refundAnalysisSlice.reducer;
export const { alertRefreshScoresInformation, resetSaveAllStatus } =
  refundAnalysisSlice.actions;
export const refundAnalysisSelector = (state: RootState) =>
  state.refundAnalysisInformation.refundAnomalyFeedback;
export const refundAnalysisDuplicateRequestedFeedbackSelector = (state: RootState) =>
  state.refundAnalysisInformation.duplicateRequestedRefundAnomalyFeedbackList;
export const refundAnalysisFrcSelector = (state: RootState) =>
  state.refundAnalysisInformation.frcList;
export const refundAnalysisEmailVerifiedSelector = (state: RootState) =>
  state.refundAnalysisInformation.userEmailValidated;
